import {
  MiniMap,
  MiniMapActions,
} from "@/components/r3f/utils/minimap/minimap";
import { SceneFilter } from "@/types/scene-filter";
import { parseVector3, useTypedEvent } from "@faro-lotv/app-component-toolbox";
import { Vector3 as Vector3Prop } from "@react-three/fiber";
import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { Camera, Vector3 } from "three";
import { useOverlayElements } from "../overlay-elements-context";
import { ImageType } from "./data-dispatch/flywalk-data-dispatch";
import { FlyWalkWaypoint } from "./flywalk-types";
import { useToolsAcquaintance } from "./tools/tools-acquaintance-context";

type FlyWalkMinimapProps = {
  /** The currently active camera */
  activeCamera: Camera;
  /** The image type of the current main element */
  imageType: ImageType;
  /** Whether the main element is a pano with valid pose */
  isPanoWithValidPose: boolean;
  /** Whether the main element is an aligned pano */
  isPanoAligned: boolean;
  /** The current scene filter */
  sceneFilter: SceneFilter;
  /** Callback issued when the user clicks a point in the minimap */
  moveCameraTo?(pos: Vector3): void;
  /** The target camera position for the minimap to animate to */
  targetCameraPosition?: Vector3Prop;
  /** Callback issued when a waypoint is clicked */
  onWaypointClicked(waypoint: FlyWalkWaypoint): void;
};

/** @returns the minimap component for the FlyWalk mode */
export function FlyWalkMinimap({
  activeCamera,
  imageType,
  isPanoWithValidPose,
  isPanoAligned,
  sceneFilter,
  moveCameraTo,
  targetCameraPosition,
  onWaypointClicked,
}: FlyWalkMinimapProps): JSX.Element | null {
  const { miniMap, minimapCanvas } = useOverlayElements();
  const miniMapActions = useRef<MiniMapActions>(null);

  const { onCameraStoppedTranslating } = useToolsAcquaintance();

  useTypedEvent(onCameraStoppedTranslating, () => {
    miniMapActions.current?.centerCameraOn(activeCamera.position.clone());
  });

  const showUserMarkerInMinimap =
    imageType !== ImageType.Pano || isPanoWithValidPose;
  const showViewConeInMinimap =
    imageType === ImageType.NoImage || isPanoAligned;

  const onMinimapClicked = useCallback(
    (pos: Vector3) => {
      if (sceneFilter !== SceneFilter.Pano) {
        // Keep the camera height as it is
        pos.y = activeCamera.position.y;
        moveCameraTo?.(pos);
      }
    },
    [activeCamera, moveCameraTo, sceneFilter],
  );

  const onTrajectoryPointClicked = useCallback(
    (eyePosition: Vector3) => {
      if (sceneFilter !== SceneFilter.Pano) {
        moveCameraTo?.(eyePosition);
      }
    },
    [moveCameraTo, sceneFilter],
  );

  const [cameraPosition, setCameraPosition] = useState<Vector3>(
    activeCamera.position.clone(),
  );

  useEffect(() => {
    if (targetCameraPosition) {
      setCameraPosition(parseVector3(targetCameraPosition));
    }
  }, [targetCameraPosition]);

  if (!miniMap) {
    return null;
  }

  return (
    <MiniMap
      actions={miniMapActions}
      canvasElement={minimapCanvas}
      trackingElement={miniMap}
      camera={activeCamera}
      cameraPosition={cameraPosition}
      enableSheetViews
      showUserMarker={showUserMarkerInMinimap}
      shouldShowViewCone={showViewConeInMinimap}
      onPlaceholderClicked={onWaypointClicked}
      onMinimapClicked={onMinimapClicked}
      onTrajectoryPointClicked={onTrajectoryPointClicked}
    />
  );
}
